import React, {useCallback, useEffect} from 'react';
import type {MutableRefObject} from 'react';
import type {AIMessageGeneratorPromptAreaProps} from '../../../aiMessageGenerator/utils/types';
import * as css from './AiAgentInput.css';
import AiAgentAttachmentPreview from './AiAgentAttachmentPreview';
import AiAgentInputActions from './AiAgentInputActions';
import {useAiAgentAttachment} from '../../hooks/useAiAgentAttachment';

const noFilePicker = () => {};

interface AiAgentInputProps extends AIMessageGeneratorPromptAreaProps {
    /** Suggestions trigger the same picker as the attach button, without a second file input. */
    openFilePickerRef?: MutableRefObject<() => void>;
}

function AiAgentInput({
    promptText,
    placeholder,
    disableSubmit,
    promptTextareaRef,
    onPromptChange,
    onPromptKeyDown,
    onSubmitPrompt,
    onPendingAttachmentChange,
    openFilePickerRef,
}: AiAgentInputProps) {
    const {
        fileInputRef,
        previewUrl,
        pendingFileName,
        convertingFileName,
        isConverting,
        acceptedFileTypes,
        handleAttachClick,
        handleFileChange,
        handlePaste,
        handleDragOver,
        handleDrop,
        handleRemoveAttachment,
        takePendingAttachment,
    } = useAiAgentAttachment(onPendingAttachmentChange);

    // Published from an effect, not from render, and withdrawn on unmount — otherwise the
    // ref would keep calling into a file input that is no longer on the page.
    useEffect(() => {
        if(!openFilePickerRef) {
            return;
        }
        openFilePickerRef.current = handleAttachClick;
        return () => {
            openFilePickerRef.current = noFilePicker;
        };
    }, [openFilePickerRef, handleAttachClick]);

    // Sending mid-conversion would send the message without the file it is waiting for.
    const handleSend = useCallback(() => {
        if(disableSubmit || isConverting) {
            return;
        }
        onSubmitPrompt({attachment: takePendingAttachment()});
    }, [disableSubmit, isConverting, onSubmitPrompt, takePendingAttachment]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if(event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
            return;
        }
        onPromptKeyDown(event);
    }, [handleSend, onPromptKeyDown]);

    return (
        <css.container onDragOver={handleDragOver} onDrop={handleDrop}>
            <AiAgentAttachmentPreview
                previewUrl={previewUrl}
                fileName={pendingFileName ?? convertingFileName}
                onRemove={handleRemoveAttachment}
            />
            <css.promptTextarea
                ref={promptTextareaRef}
                value={promptText}
                onChange={onPromptChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={placeholder}
                data-cy="ai-agent-prompt"
                rows={1}
            />
            <AiAgentInputActions
                disableSubmit={disableSubmit || isConverting}
                onAttachClick={handleAttachClick}
                onSend={handleSend}
            />
            <css.hiddenFileInput
                ref={fileInputRef}
                type="file"
                accept={acceptedFileTypes}
                onChange={handleFileChange}
                data-cy="ai-agent-file-input"
            />
        </css.container>
    );
}

export default AiAgentInput;
